import { Component, DestroyRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { faBoxes, faChevronRight, faFileExcel, faLocationDot, faTag, faWarehouse } from '@fortawesome/free-solid-svg-icons';
import { AggregateRowModel, ColumnModel, FilterSearchBeginEventArgs, FilterSettingsModel, GridComponent, PageSettingsModel, RowSelectEventArgs } from '@syncfusion/ej2-angular-grids';
import { forkJoin } from 'rxjs';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { Room } from '../../../../core/models/storage/room';
import { TenantWordsPipe } from '../../../../core/pipes/tenant-words.pipe';
import { KwFilterDropdownService } from '../../../../core/widgets/kw-filter-dropdown/kw-filter-dropdown.service';
import { FilterSettings } from '../../../../core/widgets/kw-filter-dropdown/models/filter-options';
import { FilterResults } from '../../../../core/widgets/kw-filter-dropdown/models/filter-results';
import { InventoryReportService } from '../../../pick-plan/services/inventory-reports.service';
import { InventoryService } from '../../../pick-plan/services/inventory.service';
import { InventoryItemAttributeDTO } from '../../models/item-attribute';
import { InventoryItemReportDTO } from '../../models/item-report-dto';
import { KW_QUANTITY_FORMAT } from '../../../../../@core/utils/ejs-constants';

const MOBILE_PAGE_SIZE = 25;
const UNASSIGNED_LOCATION = 'Unassigned';

interface ItemReportAttribute {
  name: string;
  value: string;
}

/** Flat grid row; each item attribute is also stored under its `attr_<id>` field so it sorts, filters and exports. */
interface ItemReportRow {
  id?: number;
  name?: string;
  warehouseId?: number;
  warehouseName?: string;
  roomId?: number;
  roomName?: string;
  roomLocationId?: number;
  locationName: string;
  lotId?: number;
  lotNumber?: string;
  inventoryQuantity?: number;
  attributeValues: ItemReportAttribute[];
  [attributeField: string]: unknown;
}

@Component({
  selector: 'inventory-item-report',
  templateUrl: './inventory-item-report.component.html',
  styleUrls: ['./inventory-item-report.component.scss']
})
export class InventoryItemReportComponent implements OnInit {
  @Input() public isMapPopup: boolean = false;
  @Input() public roomInput: Room;

  @ViewChild('locationTemplate', { static: true }) public locationTemplate: any;
  @ViewChild('actionTemplate', { static: true }) public actionTemplate: any;
  @ViewChild('grid', { static: false }) grid: GridComponent;

  public faBoxes = faBoxes;
  public faChevronRight = faChevronRight;
  public faFileExcel = faFileExcel;
  public faLocationDot = faLocationDot;
  public faTag = faTag;
  public faWarehouse = faWarehouse;

  public readonly unassignedLocation = UNASSIGNED_LOCATION;
  public isLoading = false;
  private initialLoad = true;
  private filterResults: FilterResults;
  public rows: ItemReportRow[] = [];
  private allRows: ItemReportRow[] = [];
  public columns: ColumnModel[] = [];
  public mobileVisibleCount = MOBILE_PAGE_SIZE;

  public filterSettings: FilterSettingsModel = { mode: 'Immediate', type: 'Excel' };
  public pageSettings: PageSettingsModel = { pageSize: 50, pageSizes: [50, 100, 200] };
  public aggregates: AggregateRowModel[] = [{
    columns: [
      { type: 'Count', field: 'name', footerTemplate: 'Totals:' },
      { type: 'Sum', field: 'inventoryQuantity', format: KW_QUANTITY_FORMAT, footerTemplate: '${Sum}' }
    ]
  }];

  public dropdownSettings: FilterSettings = {
    roomSettings: {
      display: true,
      singleSelection: false,
    },
    warehouseSettings: {
      display: true,
      singleSelection: false,
    },
    locationSettings: {
      display: true,
      singleSelection: false,
    },
    lotNumberSettings: {
      display: true,
      singleSelection: false,
    },
    inventoryItemSettings: {
      display: true,
      singleSelection: false,
    },
  };

  private destroyRef = inject(DestroyRef);

  constructor(
    private inventoryReportService: InventoryReportService,
    private inventoryService: InventoryService,
    private errorService: ErrorService,
    private router: Router,
    private tenantWordPipe: TenantWordsPipe,
    public filterService: KwFilterDropdownService
  ) { }

  ngOnInit() {
    this.isLoading = true;
    forkJoin([
      this.inventoryReportService.getInventoryItemsReport(false, this.roomInput?.id ?? 0),
      this.inventoryService.getInventoryItemAttributes()
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ([items, attributes]) => {
        const activeAttributes = attributes ?? [];
        this.allRows = (items ?? []).map(item => this.toRow(item, activeAttributes));
        this.setColumns(activeAttributes);
        this.applyFilters();
        this.isLoading = false;
      }, error: err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }
    });
  }

  setFilterResults(results: FilterResults) {
    if (this.initialLoad) {
      this.initialLoad = false;
      results = this.filterService.getFilterState();
    }
    this.filterResults = results;
    this.applyFilters();
  }

  actionBegin(args: FilterSearchBeginEventArgs) {
    // Excel filter lists only the first 1k distinct values by default.
    if (this.rows.length && (args.requestType === 'filterchoicerequest' || args.requestType === 'filtersearchbegin'))
      args.filterChoiceCount = this.rows.length;
  }

  onDataBound() {
    this.grid?.autoFitColumns();
  }

  onRowSelected(args: RowSelectEventArgs) {
    const row = args?.data as ItemReportRow;
    if (row) this.openLotDashboard(row);
  }

  openLotDashboard(row: ItemReportRow) {
    if (!row?.lotId) return;
    this.router.navigateByUrl('/harvest-engine/inventory/lot-dashboard/' + row.lotId);
  }

  exportExcel() {
    this.grid?.excelExport();
  }

  showMoreMobile() {
    this.mobileVisibleCount += MOBILE_PAGE_SIZE;
  }

  trackByRow(index: number, row: ItemReportRow): string {
    return `${row.id}-${row.lotId}-${row.roomLocationId}-${index}`;
  }

  private applyFilters() {
    const results = this.filterResults;
    const matches = (selected: { id?: number }[] | undefined, id: number | undefined) =>
      !selected?.length || selected.some(s => s.id === id);

    this.rows = this.allRows
      .filter(row => !results || (
        matches(results.selectedWarehouses, row.warehouseId) &&
        matches(results.selectedRooms, row.roomId) &&
        matches(results.selectedLocations, row.roomLocationId) &&
        matches(results.selectedInventoryItems, row.id) &&
        matches(results.selectedLotNumbers, row.lotId)
      ))
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    this.mobileVisibleCount = MOBILE_PAGE_SIZE;
  }

  private toRow(item: InventoryItemReportDTO, attributes: InventoryItemAttributeDTO[]): ItemReportRow {
    const row: ItemReportRow = {
      id: item.id,
      name: item.name,
      warehouseId: item.warehouseId,
      warehouseName: item.warehouseName,
      roomId: item.roomId,
      roomName: item.roomName,
      roomLocationId: item.roomLocationId,
      locationName: item.roomLocationName || UNASSIGNED_LOCATION,
      lotId: item.lotId,
      lotNumber: item.lotNumber,
      inventoryQuantity: item.inventoryQuantity,
      attributeValues: []
    };

    for (const attribute of attributes) {
      const value = item.attributes?.find(a => a.inventoryItemAttributeId === attribute.id)?.value ?? '';
      row[this.attributeField(attribute)] = value;
      if (value)
        row.attributeValues.push({ name: attribute.name ?? '', value });
    }
    return row;
  }

  private setColumns(attributes: InventoryItemAttributeDTO[]) {
    const word = (w: string) => this.tenantWordPipe.transform(w);
    this.columns = [
      { field: 'name', headerText: word('Product'), autoFit: true, clipMode: 'EllipsisWithTooltip' },
      { field: 'warehouseName', headerText: word('Warehouse'), autoFit: true, clipMode: 'EllipsisWithTooltip' },
      { field: 'roomName', headerText: word('Room'), autoFit: true, clipMode: 'EllipsisWithTooltip' },
      { field: 'locationName', headerText: word('Location'), autoFit: true, template: this.locationTemplate },
      { field: 'lotNumber', headerText: 'Lot Number', autoFit: true, clipMode: 'EllipsisWithTooltip' },
      ...attributes.map(attribute => <ColumnModel>{
        field: this.attributeField(attribute),
        headerText: attribute.name,
        autoFit: true,
        clipMode: 'EllipsisWithTooltip'
      }),
      { field: 'inventoryQuantity', headerText: 'Quantity', autoFit: true, format: KW_QUANTITY_FORMAT, textAlign: 'Right' },
      { headerText: '', template: this.actionTemplate, width: 56, allowSorting: false, allowFiltering: false, textAlign: 'Center' }
    ];
  }

  private attributeField(attribute: InventoryItemAttributeDTO): string {
    return `attr_${attribute.id}`;
  }
}
